/*
 * MHD-Deal-Popup
 * --------------
 * Faengt den Klick auf "Zur Kasse" ab und zeigt vorher die Artikel mit dem
 * kuerzesten Mindesthaltbarkeitsdatum. Add-to-Cart laeuft ueber denselben Weg
 * wie das Produktformular in theme.js (cart:grouped-sections -> /cart/add.js ->
 * pubsub cartUpdate), damit Drawer, Warenkorbseite und das Versandziel im
 * Drawer sich ohne Reload aktualisieren.
 */
(function () {
  const STORAGE_KEY = 'sb:mhd-deal-popup';

  class MhdDealPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.grid = this.querySelector('.mhd-deal-popup__grid');
      this.cards = Array.from(this.querySelectorAll('.mhd-deal-card'));
      this.maxVisible = parseInt(this.dataset.maxVisible, 10) || 4;
      this.frequency = this.dataset.frequency || 'session';
      this.addLabel = this.dataset.addLabel || 'Mitnehmen';
      this.addedLabel = this.dataset.addedLabel || 'Im Warenkorb';
      this.errorLabel = this.dataset.errorLabel || 'Konnte nicht hinzugefügt werden.';

      this.isOpen = false;
      this.bypass = false;
      this.triggerButton = null;
      this.cartVariantIds = new Set();
      this.readCartIds(this.dataset.cartVariantIds);

      // Dringlichkeit schlaegt Rabatthoehe: kuerzestes Restdatum nach oben.
      // MegaDeals landen dadurch von selbst vorne, ohne Sonderregel.
      // Wichtig: this.cards selbst umsortieren, nicht nur eine Kopie fuer die
      // DOM-Reihenfolge - applyFilter() waehlt die sichtbaren Karten aus dieser
      // Liste aus, sonst zeigt das Popup nicht die dringendsten Artikel.
      this.cards.sort((a, b) => Number(a.dataset.days) - Number(b.dataset.days));
      this.cards.forEach((card) => this.grid.appendChild(card));

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onPopupClick.bind(this));
      this.addEventListener('change', this.onVariantChange.bind(this));

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
      }

      this.applyFilter();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    readCartIds(raw) {
      try {
        JSON.parse(raw || '[]').forEach((id) => this.cartVariantIds.add(String(id)));
      } catch (error) {
        // Kein harter Fehler - dann wird eben nichts ausgefiltert.
      }
    }

    /**
     * Blendet Artikel aus, die schon im Warenkorb liegen, und begrenzt den Rest
     * auf die eingestellte Anzahl. Gibt zurueck, wie viele sichtbar sind.
     */
    applyFilter() {
      let visible = 0;

      this.cards.forEach((card) => {
        const inCart = this.cartVariantIds.has(String(card.dataset.variantId));
        const show = !inCart && visible < this.maxVisible;
        card.hidden = !show;
        if (show) visible++;
      });

      this.visibleCount = visible;
      return visible;
    }

    hasBeenSeen() {
      if (this.frequency === 'always') return false;
      try {
        if (this.frequency === 'day') {
          return localStorage.getItem(STORAGE_KEY) === new Date().toDateString();
        }
        return sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (error) {
        return false;
      }
    }

    markSeen() {
      if (this.frequency === 'always') return;
      try {
        if (this.frequency === 'day') {
          localStorage.setItem(STORAGE_KEY, new Date().toDateString());
        } else {
          sessionStorage.setItem(STORAGE_KEY, '1');
        }
      } catch (error) {
        // Privater Modus o.ae. - dann greift die Frequenzbegrenzung eben nicht.
      }
    }

    onDocumentClick(event) {
      const button = event.target.closest && event.target.closest('button[name="checkout"]');
      if (!button) return;
      if (this.bypass || this.isOpen) return;
      if (this.hasBeenSeen()) return;
      if (this.applyFilter() === 0) return;

      event.preventDefault();
      event.stopPropagation();

      this.triggerButton = button;
      this.open();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }

    onPopupClick(event) {
      const addButton = event.target.closest('[data-mhd-add]');
      if (addButton) {
        this.addToCart(addButton);
        return;
      }

      if (event.target.closest('[data-mhd-continue]')) {
        this.continueToCheckout();
        return;
      }

      if (event.target.closest('[data-mhd-close]')) {
        this.close();
      }
    }

    /**
     * Variantenwechsel innerhalb der Karte: Preis, Streichpreis, Rabatt-Badge,
     * Restbestand und die Ziel-Variante des Buttons ziehen mit. Der Kunde
     * bleibt dabei im Popup - kein Absprung auf die Produktseite.
     */
    onVariantChange(event) {
      const select = event.target.closest('[data-mhd-variants]');
      if (!select) return;

      const card = select.closest('.mhd-deal-card');
      const option = select.selectedOptions[0];
      if (!card || !option) return;

      card.dataset.variantId = option.value;

      const price = card.querySelector('.mhd-deal-card__price');
      if (price) price.textContent = option.dataset.price || '';

      const compare = card.querySelector('.mhd-deal-card__compare');
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }

      const discount = card.querySelector('.mhd-deal-card__discount');
      if (discount) {
        discount.textContent = `-${option.dataset.discount}%`;
        discount.hidden = !option.dataset.discount;
      }

      const stock = card.querySelector('.mhd-deal-card__stock');
      if (stock) {
        stock.textContent = option.dataset.stock || '';
        stock.hidden = !option.dataset.stock;
      }

      // Die neue Variante ist noch nicht hinzugefuegt - also zurueck auf Anfang,
      // auch wenn die vorher gewaehlte schon im Warenkorb lag.
      const button = card.querySelector('[data-mhd-add]');
      if (!button) return;

      button.dataset.variantId = option.value;

      const inCart = this.cartVariantIds.has(String(option.value));
      const label = button.querySelector('.mhd-deal-card__add-label') || button;
      label.textContent = inCart ? this.addedLabel : this.addLabel;
      card.classList.toggle('mhd-deal-card--added', inCart);
      card.classList.remove('mhd-deal-card--error');
      if (inCart) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    onCartUpdate(event) {
      const cart = event && event.cart;
      if (!cart || !Array.isArray(cart.items)) return;

      this.cartVariantIds = new Set(cart.items.map((item) => String(item.variant_id)));

      // Solange das Popup offen ist, nicht neu filtern - sonst verschwindet die
      // Karte unter dem Finger, sobald der Artikel hinzugefuegt wurde.
      if (!this.isOpen) this.applyFilter();
    }

    open() {
      this.markSeen();
      this.hidden = false;
      // Reflow erzwingen statt requestAnimationFrame: rAF wird in inaktiven
      // Tabs gedrosselt, dann bliebe das Popup auf opacity 0 stehen.
      void this.offsetWidth;
      this.classList.add('is-open');
      document.body.classList.add('mhd-deal-popup-open');
      this.isOpen = true;

      const continueButton = this.querySelector('[data-mhd-continue]');
      if (continueButton) continueButton.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('mhd-deal-popup-open');
      this.isOpen = false;
      this.applyFilter();

      const finish = () => {
        if (!this.isOpen) this.hidden = true;
      };
      setTimeout(finish, 300);

      if (this.triggerButton) this.triggerButton.focus({ preventScroll: true });
    }

    continueToCheckout() {
      const button = this.triggerButton;
      this.bypass = true;
      this.close();

      if (!button) {
        window.location.href = '/checkout';
        return;
      }

      const form = button.form;
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
      } else if (form) {
        // Fallback fuer Browser ohne requestSubmit: name/value des Buttons
        // wuerden bei form.submit() fehlen, deshalb explizit mitschicken.
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = '';
        form.appendChild(hidden);
        form.submit();
      } else {
        button.click();
      }
    }

    get fetchConfig() {
      if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig) {
        return FoxTheme.utils.fetchConfig('javascript');
      }
      return {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
      };
    }

    get cartRoutes() {
      const routes = (window.FoxTheme && FoxTheme.routes) || {};
      return {
        add: routes.cart_add_url || '/cart/add',
        cart: routes.cart_url || '/cart',
      };
    }

    async addToCart(button) {
      if (button.hasAttribute('aria-disabled')) return;

      const card = button.closest('.mhd-deal-card');
      const label = button.querySelector('.mhd-deal-card__add-label') || button;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('btn--loading');
      if (card) card.classList.remove('mhd-deal-card--error');

      try {
        // Der Cart-Drawer haengt seine eigene Section-ID hier ein, damit die
        // Antwort das fertig gerenderte Markup mitliefert.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);

        const config = this.fetchConfig;
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(this.cartRoutes.add, config);
        const parsed = await response.json();

        if (parsed.status) throw new Error(parsed.description || parsed.message);

        const cartResponse = await fetch(this.cartRoutes.cart, {
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        });
        const cartJson = await cartResponse.json();
        cartJson.sections = parsed.sections;

        this.cartVariantIds.add(String(button.dataset.variantId));

        if (window.FoxTheme && FoxTheme.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        }

        label.textContent = this.addedLabel;
        if (card) card.classList.add('mhd-deal-card--added');
      } catch (error) {
        console.error('[mhd-deal-popup]', error);
        label.textContent = this.errorLabel;
        if (card) card.classList.add('mhd-deal-card--error');
        button.removeAttribute('aria-disabled');
      } finally {
        button.classList.remove('btn--loading');
      }
    }
  }

  if (!customElements.get('mhd-deal-popup')) {
    customElements.define('mhd-deal-popup', MhdDealPopup);
  }
})();
